import { type RefObject } from 'react';
import styled from 'styled-components';
import {
  ComponentMatrix2Commands,
  LevelHeaderViewModel,
  ViewpointModeComponentMatrixViewModel,
} from '../types';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors, lineHeight, s4, s8 } from '@ardoq/design-tokens';
import { caption, text2 } from '@ardoq/typography';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { IconButton, ButtonSize } from '@ardoq/button';
import {
  LEVEL_HEADER_CARD_MAX_WIDTH,
  LEVEL_HEADER_CARD_MIN_WIDTH,
} from '../consts';
import { ButtonWithDropdown, DropdownOptionType } from '@ardoq/dropdown-menu';
import { t, type I18nLocale } from '@ardoq/i18n';

const LevelHeaderIconButton = styled(IconButton)`
  min-height: 26px;
`;

type LevelLabelViewProps = LevelHeaderViewModel & {
  axisLabel: string;
  iconName: IconName;
  options: ReturnType<typeof getRowOptions>;
  i18nLocale: I18nLocale;
  expandToFill: boolean;
};

const getRowOptions = (
  i18nLocale: I18nLocale,
  commands: ComponentMatrix2Commands
) => [
  {
    type: DropdownOptionType.BUTTON_OPTION,
    label: t(
      i18nLocale,
      'tabview:componentMatrix.levelHeader.collapseAllRows',
      'Collapse all rows'
    ),
    onClick: commands.onCollapseAllRowsClick,
  },
  {
    type: DropdownOptionType.BUTTON_OPTION,
    label: t(
      i18nLocale,
      'tabview:componentMatrix.levelHeader.expandAllRows',
      'Expand all rows'
    ),
    onClick: commands.onExpandAllRowsClick,
  },
];

const getColumnOptions = (
  i18nLocale: I18nLocale,
  commands: ComponentMatrix2Commands
) => [
  {
    type: DropdownOptionType.BUTTON_OPTION,
    label: t(
      i18nLocale,
      'tabview:componentMatrix.levelHeader.collapseAllColumns',
      'Collapse all columns'
    ),
    onClick: commands.onCollapseAllColumnsClick,
  },
  {
    type: DropdownOptionType.BUTTON_OPTION,
    label: t(
      i18nLocale,
      'tabview:componentMatrix.levelHeader.expandAllColumns',
      'Expand all columns'
    ),
    onClick: commands.onExpandAllColumnsClick,
  },
];

const LevelLabel = ({
  fieldName,
  axisLabel,
  iconName,
  referenceDirection,
  options,
  i18nLocale,
  expandToFill,
}: LevelLabelViewProps) => (
  <LevelLabelCard $expandToFill={expandToFill}>
    <LevelHeaderTitleRow>
      <AxisLabelGroup>
        <Icon iconName={iconName} iconSize={IconSize.SMALL} />
        <AxisText>{axisLabel}</AxisText>
      </AxisLabelGroup>
      <ButtonWithDropdown
        options={options}
        alignMenuTo="right"
        renderDropdownButton={(
          hostRef: RefObject<HTMLElement | null>,
          _isOpen: boolean,
          openModal: VoidFunction
        ) => (
          <LevelHeaderIconButton
            ref={hostRef as RefObject<HTMLButtonElement | null>}
            iconName={IconName.MORE_HORIZ}
            label={t(
              i18nLocale,
              'tabview:componentMatrix.levelHeader.moreOptions',
              'More options'
            )}
            buttonSize={ButtonSize.SMALL}
            onClick={openModal}
          />
        )}
      />
    </LevelHeaderTitleRow>
    <FieldNameRow>
      <FieldNameText>
        <TruncatedTextPopover>{fieldName}</TruncatedTextPopover>
      </FieldNameText>
      {referenceDirection && (
        <ReferenceDirectionText>
          {referenceDirection === 'outgoing'
            ? t(
                i18nLocale,
                'tabview:componentMatrix.levelHeader.outgoing',
                '(Outgoing)'
              )
            : t(
                i18nLocale,
                'tabview:componentMatrix.levelHeader.incoming',
                '(Incoming)'
              )}
        </ReferenceDirectionText>
      )}
    </FieldNameRow>
  </LevelLabelCard>
);

const LevelHeadersContainer = styled.div`
  grid-row: 1;
  grid-column: 1;
  display: flex;
  flex-direction: row;
  gap: ${s8};
  align-items: stretch;
  /* 4px right padding combines with BODY_CELL_CONTAINER_HORIZONTAL_MARGIN
   * on the first column header for an 8px visible gap. */
  padding: 0 ${s4} 0 0;
`;

const LevelLabelCard = styled.div<{ $expandToFill: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  /* When this card is alone in the level-headers area (the other axis has
   * no field selected), drop the max-width so it spans the full row-headers
   * area width below — ARD-31376. */
  ${({ $expandToFill }) =>
    $expandToFill ? '' : `max-width: ${LEVEL_HEADER_CARD_MAX_WIDTH}px;`}
  min-width: ${LEVEL_HEADER_CARD_MIN_WIDTH}px;
  gap: ${s4};
  padding: ${s4} ${s8} ${s8};
  background-color: ${colors.surfaceStatusNeutral};
  border: 1px solid ${colors.borderAction};
  border-radius: 8px;
  overflow: hidden;
`;

const LevelHeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${s4};
`;

// No min-width / overflow constraints: the Figma spec sizes the card so the
// axis label ("Row" / "Column") fits at LEVEL_HEADER_CARD_MIN_WIDTH (112px)
// without truncation. If the more-options button is tight on space the
// flex row will push it instead of ellipsizing the label.
const AxisLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${s4};
  ${caption};
  color: ${colors.textSubtle};
  white-space: nowrap;
`;

const AxisText = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

const FieldNameRow = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const FieldNameText = styled.span`
  ${text2};
  color: ${colors.textDefault};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  height: ${lineHeight.regular};
`;

const ReferenceDirectionText = styled.span`
  ${text2};
  color: ${colors.textDefault};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

type ComponentMatrixLevelHeadersAreaProperties = {
  viewModel: ViewpointModeComponentMatrixViewModel;
  i18nLocale: I18nLocale;
  commands: ComponentMatrix2Commands;
};

const ComponentMatrixLevelHeadersArea = ({
  viewModel: {
    measurements: { mainContainerLeftColumnWidth },
    rowLevelHeader,
    columnLevelHeader,
  },
  i18nLocale,
  commands,
}: ComponentMatrixLevelHeadersAreaProperties) => {
  if (mainContainerLeftColumnWidth === 0) return null;
  if (!rowLevelHeader && !columnLevelHeader) return null;

  const isLoneCard = !rowLevelHeader || !columnLevelHeader;

  return (
    // Pin to the JS-computed `mainContainerLeftColumnWidth` so the
    // auto-sized col 1 track can't grow past it when the cards' natural
    // text width slightly exceeds `LEVEL_HEADER_CARD_MIN_WIDTH` — without
    // this, `flex: 1 0 0` cards expand sub-pixel-ly and end up wider than
    // the row-headers below (e.g. 112.78px vs 112px), creating a ~2px
    // misalignment with `ComponentMatrixRowHeaderContainer` which has an
    // explicit `width={mainContainerLeftColumnWidth}` inline. ARD-31376.
    <LevelHeadersContainer style={{ width: mainContainerLeftColumnWidth }}>
      {rowLevelHeader && (
        <LevelLabel
          {...rowLevelHeader}
          axisLabel={t(
            i18nLocale,
            'tabview:componentMatrix.levelHeader.row',
            'Row'
          )}
          iconName={IconName.TABLE_ROWS}
          options={getRowOptions(i18nLocale, commands)}
          i18nLocale={i18nLocale}
          expandToFill={isLoneCard}
        />
      )}
      {columnLevelHeader && (
        <LevelLabel
          {...columnLevelHeader}
          axisLabel={t(
            i18nLocale,
            'tabview:componentMatrix.levelHeader.column',
            'Column'
          )}
          iconName={IconName.TABLE_COLUMNS}
          options={getColumnOptions(i18nLocale, commands)}
          i18nLocale={i18nLocale}
          expandToFill={isLoneCard}
        />
      )}
    </LevelHeadersContainer>
  );
};

export { ComponentMatrixLevelHeadersArea };
